import './style.css';

import { Env } from '@env';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import PersonaReact from 'persona-react';
import React from 'react';
import { ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useReservation } from '@/api/reservations/use-reservation';
import { findAdditionalDriver } from '@/components/additional-drivers/status';
import VerificationScreen from '@/components/verification-screen';
import { usePoll } from '@/core/hooks/use-poll';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Lock } from '@/ui/icons';
import { Id } from '@/ui/icons/id';
import { ListItem } from '@/ui/list-item';
import { Loader } from '@/ui/loader';

import Footer from '../footer';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

export default function VerifyLicense() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { trip_id, reverify } = useLocalSearchParams();
  const [status, setStatus] = React.useState<
    'uploaded' | 'successful' | 'rejected' | ''
  >('');
  const [showPersonaFlow, setShowPersonaFlow] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [showVerificationScreenLoader, setShowVerificationScreenLoader] =
    React.useState(false);

  const { data: customer, isFetching: isFetchingCustomer } = useCustomer();

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: trip_id },
    enabled: !!trip_id,
  });

  const additionalDriver = React.useMemo(() => {
    if (!customer || !resData) return null;

    return findAdditionalDriver(resData?.additionalDrivers, customer);
  }, [resData, customer]);

  const { isPolling, poll: pollForLicenseVerification } = usePoll(
    refetch,
    (data) => {
      if (additionalDriver) {
        return (
          findAdditionalDriver(data.additionalDrivers, customer)
            ?.driversLicenseStatus === 'approved'
        );
      }

      return data.checkin
        ? !!data.checkin.licenseVerified
        : data.customer.driversLicenseStatus === 'approved';
    }
  );

  useFocusEffect(
    React.useCallback(() => {
      setStatus('');
    }, [])
  );

  React.useEffect(() => {
    if (reverify && customer && status !== '') {
      setShowVerificationScreenLoader(true);

      setTimeout(() => {
        setShowVerificationScreenLoader(false);
      }, 2000);
      return;
    }
  }, [reverify, customer, status]);

  if (isFetchingReservation || showVerificationScreenLoader || isPolling) {
    return <Loader />;
  }

  if (customer && status !== '') {
    return (
      <VerificationScreen
        status={status}
        name="license"
        onGoBack={() => {
          if (reverify) {
            router.navigate(`/trips/${trip_id}/checkin`);
            return;
          }

          if (
            additionalDriver &&
            (!additionalDriver.setupComplete ||
              additionalDriver.driversLicenseStatus !== 'approved')
          ) {
            router.navigate(`/trips/${trip_id}/additional-drivers/verify`);
            return;
          }

          router.navigate(`/trips/${trip_id}/details`);
          return;
        }}
      />
    );
  }

  if (isFetchingCustomer) {
    return <Loader />;
  }

  return (
    <ScrollView
      contentContainerStyle={styles.contentContainerStyle(showPersonaFlow)}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container(showPersonaFlow)}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">Verify Your Driver's License</Text>
          </View>
        ) : null}

        {showPersonaFlow ? (
          <View style={{ flexGrow: 1 }}>
            <PersonaReact
              templateId="itmpl_cWVDdKTpvPswnbHLQykFWKY4"
              environmentId={Env.PERSONA_ENV_ID}
              referenceId={customer.publicToken}
              onComplete={async ({ inquiryId, status, fields }) => {
                const verified = await pollForLicenseVerification();
                setStatus(verified ? 'successful' : 'rejected');
              }}
              onCancel={() => {}}
              onError={(error) => {
                setStatus('rejected');
              }}
            />
          </View>
        ) : (
          <View style={{ flexGrow: 1, justifyContent: 'space-between' }}>
            <View style={{ gap: 24 }}>
              <View>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  You must verify your license to be approved to drive.
                  Verification typically takes just a few minutes.
                </Text>
              </View>

              <View style={{ gap: 16 }}>
                <ListItem
                  text="Prepare your driver's license"
                  caption="We accept American and international driver's licenses"
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Id />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                    />
                  }
                  onPress={() => {}}
                  withBorder={false}
                />

                <ListItem
                  text="Security"
                  caption="We’ll securely verify your identity by matching your face with your driver’s license to ensure a safe experience."
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Lock />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                    />
                  }
                  onPress={() => {}}
                  withBorder={false}
                />
              </View>
            </View>

            <View>
              <Button
                text="Verify License"
                type="primary"
                loading={isVerifying}
                disabled={isVerifying}
                onPress={() => {
                  if (isVerifying) return;
                  setIsVerifying(true);
                  setShowPersonaFlow(true);
                }}
              />
            </View>
          </View>
        )}
      </View>

      <Footer />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  contentContainerStyle: (showPersonaFlow: boolean) => ({
    flexGrow: 1,
    gap: 32,
    alignItems: {
      xs: showPersonaFlow ? 'stretch' : 'center',
      md: 'center',
    },
  }),
  container: (showPersonaFlow: boolean) => ({
    flexGrow: 1,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    padding: 16,
    height: {
      md: showPersonaFlow ? 1000 : 800,
    },
  }),
}));
